<script setup lang="ts">
import { ask } from '../dialogs';
import { computed, onMounted, ref } from 'vue';
import type { LogRow } from '@vp/core';
import { logger, storage } from '../platform';
import { locale, t } from '../i18n';
import { download } from '../export';
import AppNav from '../components/AppNav.vue';
import Icon from '../components/Icon.vue';

defineProps<{ embedded?: boolean }>();

/** Local diagnostics journal (§14.1, §14.2): level filter, search, entries grouped by day. */
type Level = LogRow['level'];
const LIMIT = 2000;
const rows = ref<LogRow[]>([]);
const level = ref<'' | Level>('');
const query = ref('');
const loading = ref(true);
const open = ref<number | null>(null);

async function load(): Promise<void> {
  loading.value = true;
  await logger.flush();
  rows.value = await storage.queryLogs({ limit: LIMIT });
  loading.value = false;
}

onMounted(load);

const LEVELS: { id: Level; label: string }[] = [
  { id: 'error', label: 'logs.errors' },
  { id: 'warn', label: 'logs.warnings' },
  { id: 'info', label: 'logs.info' },
  { id: 'debug', label: 'logs.debug' },
];

const counts = computed(() => {
  const c: Record<Level, number> = { error: 0, warn: 0, info: 0, debug: 0 };
  for (const r of rows.value) c[r.level]++;
  return c;
});

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  return rows.value.filter((r) => (!level.value || r.level === level.value) && (!q || r.code.toLowerCase().includes(q) || (r.data && JSON.stringify(r.data).toLowerCase().includes(q))));
});

const hasContent = computed(() => rows.value.some((r) => r.verbose));

const tag = computed(() => (locale.value === 'ru' ? 'ru-RU' : 'en-US'));

function dayKey(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dayLabel(ts: number): string {
  const now = new Date();
  if (dayKey(ts) === dayKey(now.getTime())) return t('logs.today');
  if (dayKey(ts) === dayKey(now.getTime() - 86400000)) return t('logs.yesterday');
  return new Date(ts).toLocaleDateString(tag.value, { day: 'numeric', month: 'long', year: 'numeric' });
}

const groups = computed(() => {
  const out: { key: string; label: string; rows: LogRow[] }[] = [];
  for (const r of filtered.value) {
    const k = dayKey(r.ts);
    if (out[out.length - 1]?.key !== k) out.push({ key: k, label: dayLabel(r.ts), rows: [] });
    out[out.length - 1].rows.push(r);
  }
  return out;
});

function time(ts: number): string {
  return new Date(ts).toLocaleTimeString(tag.value, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function short(v: unknown): string {
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  return s.length > 40 ? s.slice(0, 39) + '…' : s;
}

function entries(r: LogRow): [string, unknown][] {
  return r.data ? Object.entries(r.data) : [];
}

function toggle(r: LogRow): void {
  if (r.id === undefined || !r.data) return;
  open.value = open.value === r.id ? null : r.id;
}

async function exportLogs(): Promise<void> {
  const all = await storage.queryLogs({ limit: 100000 });
  if (all.some((r) => r.verbose) && !(await ask(t('logs.contentWarn')))) return;
  download(new Blob([JSON.stringify(all, null, 2)], { type: 'application/json' }), `vp-log-${new Date().toISOString().slice(0, 10)}.json`);
}

async function clear(): Promise<void> {
  if (!(await ask(t('logs.confirmClear'), { okLabel: t('logs.clear'), danger: true }))) return;
  await storage.clearLogs();
  await load();
}
</script>

<template>
  <div class="wrap" :class="{ embedded }">
    <AppNav v-if="!embedded" />
    <main class="page">
      <div class="page-inner">
        <div class="head">
          <div>
            <h1 class="title">{{ t('logs.title') }}</h1>
            <p class="sub">{{ t('logs.retention') }}</p>
          </div>
          <div class="actions">
            <button class="pill" :title="t('logs.refresh')" @click="load"><Icon name="refresh" :size="14" />{{ t('logs.refresh') }}</button>
            <button class="pill" :disabled="!rows.length" @click="exportLogs"><Icon name="download" :size="14" />{{ t('logs.export') }}</button>
            <button class="pill danger" :disabled="!rows.length" @click="clear"><Icon name="trash" :size="14" />{{ t('logs.clear') }}</button>
          </div>
        </div>

        <div class="filters">
          <div class="seg-pills" role="radiogroup" :aria-label="t('logs.level')">
            <button role="radio" :aria-checked="level === ''" :class="{ active: level === '' }" @click="level = ''">
              {{ t('logs.all') }} <span class="count">{{ rows.length }}</span>
            </button>
            <button v-for="l in LEVELS" :key="l.id" role="radio" :aria-checked="level === l.id" :class="{ active: level === l.id }" :disabled="!counts[l.id]" @click="level = l.id">
              <span class="dot" :class="l.id"></span>{{ t(l.label) }} <span class="count">{{ counts[l.id] }}</span>
            </button>
          </div>
          <label class="search">
            <Icon name="search" :size="14" />
            <input v-model="query" type="search" :placeholder="t('logs.search')" :aria-label="t('logs.search')" />
          </label>
        </div>

        <p v-if="hasContent" class="note"><Icon name="warning" :size="14" />{{ t('logs.contentWarn').split('.')[0] }}.</p>

        <p v-if="!loading && !filtered.length" class="empty">{{ t('logs.empty') }}</p>

        <section v-for="g in groups" :key="g.key" class="day">
          <h2>{{ g.label }}</h2>
          <ul>
            <li v-for="r in g.rows" :key="r.id" :class="[r.level, { open: open === r.id, clickable: !!r.data }]" @click="toggle(r)">
              <div class="line">
                <span class="dot" :class="r.level" :title="r.level"></span>
                <span class="time">{{ time(r.ts) }}</span>
                <span class="code">{{ r.code }}</span>
                <span v-if="open !== r.id" class="kv">
                  <span v-for="[k, v] in entries(r).slice(0, 4)" :key="k" class="chip"><span class="k">{{ k }}</span> {{ short(v) }}</span>
                  <span v-if="entries(r).length > 4" class="chip more">+{{ entries(r).length - 4 }}</span>
                </span>
              </div>
              <pre v-if="open === r.id" class="data">{{ JSON.stringify(r.data, null, 2) }}</pre>
            </li>
          </ul>
        </section>
        <p v-if="rows.length >= LIMIT" class="sub">{{ t('logs.shown', { n: rows.length, total: LIMIT + '+' }) }}</p>
      </div>
    </main>
  </div>
</template>

<style scoped>
.wrap {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.wrap.embedded {
  height: auto;
}
.wrap.embedded .page {
  flex: none;
  height: auto;
  overflow: visible;
}
.wrap.embedded .page-inner {
  max-width: none;
  margin: 0;
  padding: 0;
}
.page {
  /* the header takes 64 px: the page gets the rest instead of a full 100% (no outer scroll) */
  flex: 1;
  min-height: 0;
  height: auto;
}
.page-inner {
  max-width: 980px;
  padding-top: 8px;
}
.head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}
.title {
  margin: 0 0 4px 4px;
  font-size: 22px;
  font-weight: 600;
}
.sub {
  margin: 0 0 0 4px;
  font-size: 12px;
  color: var(--text-3);
}
.actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.pill {
  height: 32px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
}
.pill.danger {
  color: var(--danger);
}
.filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 18px 0 10px;
}
.filters .seg-pills {
  background: var(--panel);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  padding: 3px;
}
.filters .seg-pills button {
  padding: 0 12px;
  gap: 6px;
}
.filters .seg-pills button.active {
  background: transparent;
  color: var(--text);
  box-shadow: none;
}
.filters .seg-pills button:disabled {
  opacity: 0.4;
  cursor: default;
}
.count {
  font-size: 11px;
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}
.search {
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 14px;
  background: var(--panel);
  border-radius: 999px;
  color: var(--text-3);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}
.search input {
  flex: 1;
  border: none;
  outline: none;
  background: none;
  min-width: 0;
}
.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
  background: #94a3b8;
}
.dot.error {
  background: var(--danger);
}
.dot.warn {
  background: #f59e0b;
}
.dot.info {
  background: #3b82f6;
}
.dot.debug {
  background: var(--border-strong);
}
.note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 12px;
  background: var(--warn-soft);
  color: var(--warn);
  font-size: 12px;
}
.empty {
  margin: 40px 0;
  text-align: center;
  color: var(--text-3);
}
.day h2 {
  margin: 18px 0 6px 4px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-3);
}
ul {
  list-style: none;
  margin: 0;
  padding: 4px;
  background: var(--panel);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05), 0 4px 16px rgba(15, 23, 42, 0.04);
}
li {
  border-radius: 12px;
  padding: 0 10px;
}
li.clickable {
  cursor: pointer;
}
li.clickable:hover {
  background: var(--panel-2);
}
li.error {
  background: var(--danger-soft);
}
li.open {
  background: var(--hover);
}
.line {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 34px;
  min-width: 0;
}
.time {
  font-size: 12px;
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
  flex: none;
}
.code {
  font-family: ui-monospace, Consolas, monospace;
  font-size: 12px;
  color: var(--text);
  flex: none;
}
.kv {
  display: flex;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
}
.chip {
  font-family: ui-monospace, Consolas, monospace;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--hover);
  color: var(--text-2);
  white-space: nowrap;
}
.chip .k {
  color: var(--text-3);
}
.chip.more {
  color: var(--text-3);
}
.data {
  margin: 0 0 10px 18px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--panel);
  font-size: 11.5px;
  line-height: 1.5;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
}
@media (max-width: 640px) {
  .kv {
    display: none;
  }
}
</style>
